import { ArrowLeft } from "@medusajs/icons"
import { Button, Checkbox, Container, Input, Skeleton, Table, Text } from "@medusajs/ui"
import { useEffect, useState, type ReactNode } from "react"
import { Link } from "react-router-dom"
import { Badge, type Tone } from "@/terabizz"

export function PageHeader({ title, subtitle, back, actions }: { title: ReactNode; subtitle?: ReactNode; back?: string; actions?: ReactNode }) {
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-x-1.5">
        {back && (
          <Link to={back} className="mt-0.5 rounded-[8px] p-1.5 text-[var(--tb-ink-3)] transition-colors hover:bg-[color:var(--k-overlay-hover)]" aria-label="Back">
            <ArrowLeft />
          </Link>
        )}
        <div>
          <h1 className="k-h1">{title}</h1>
          {subtitle && (
            <Text size="small" className="mt-1 text-ui-fg-subtle">
              {subtitle}
            </Text>
          )}
        </div>
      </div>
      {actions && <div className="flex items-center gap-x-2">{actions}</div>}
    </div>
  )
}

/** Shopify card: bold 14px title without a divider under it, 16px padding. */
export function Section({ title, actions, children, className }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <Container className={"tf-card p-0 " + (className ?? "")}>
      {title && (
        <div className="tf-card-head flex min-h-[44px] items-center justify-between gap-x-3 px-4 pb-1 pt-3">
          <h2 className="k-h3">{title}</h2>
          {actions}
        </div>
      )}
      <div className="tf-card-body divide-y">{children}</div>
    </Container>
  )
}

/** Product/collection thumbnail: fixed size, never squashed. */
export function Thumb({ src, size = 40 }: { src?: string | null; size?: number }) {
  const [broken, setBroken] = useState<string | null>(null)
  return src && broken !== src ? (
    <img src={src} alt="" onError={() => setBroken(src)} style={{ width: size, height: size }} className="shrink-0 rounded-lg border border-ui-border-base object-cover" />
  ) : (
    <div style={{ width: size, height: size }} className="flex shrink-0 items-center justify-center rounded-lg border border-ui-border-base bg-ui-bg-subtle text-ui-fg-muted">
      <svg width={Math.round(size * 0.45)} height={Math.round(size * 0.45)} viewBox="0 0 20 20" fill="none" aria-hidden>
        <rect x="2.5" y="3.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="7" cy="8" r="1.3" fill="currentColor" />
        <path d="M3 14l4-3.5 3 2.5 3-3 4 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </div>
  )
}

/** Thumbnail for a library file: the image itself, or a typed tile (PDF, video) so documents never look like broken pictures. */
export function FileThumb({ mime, src, size = 40 }: { mime: string; src: string; size?: number }) {
  if (mime.startsWith("image/")) return <Thumb src={src} size={size} />
  const pdf = mime === "application/pdf"
  const tone = pdf ? "#c0392b" : "#3b5ccc"
  return (
    <div style={{ width: size, height: size }} className="flex shrink-0 flex-col items-center justify-center gap-px rounded-lg border border-ui-border-base bg-ui-bg-base" aria-hidden>
      {pdf ? (
        <svg width={Math.round(size * 0.42)} height={Math.round(size * 0.42)} viewBox="0 0 20 20" fill="none"><path d="M5 2.5h6.5L15.5 6.5v11a1 1 0 01-1 1h-9.5a1 1 0 01-1-1v-14a1 1 0 011-1z" stroke={tone} strokeWidth="1.4" strokeLinejoin="round" /><path d="M11.5 2.5v4h4" stroke={tone} strokeWidth="1.4" strokeLinejoin="round" /></svg>
      ) : (
        <svg width={Math.round(size * 0.42)} height={Math.round(size * 0.42)} viewBox="0 0 20 20" fill="none"><rect x="2.5" y="4" width="15" height="12" rx="2" stroke={tone} strokeWidth="1.4" /><path d="M8.5 7.5v5l4-2.5-4-2.5z" fill={tone} /></svg>
      )}
      <span style={{ color: tone, fontSize: Math.max(8, Math.round(size * 0.2)) }} className="font-semibold leading-none">{pdf ? "PDF" : "VIDEO"}</span>
    </div>
  )
}

export function EmptyState({ title, body, action }: { title: string; body?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-y-2 px-4 py-12 text-center">
      <Text weight="plus">{title}</Text>
      {body && (
        <Text size="small" className="max-w-md text-ui-fg-subtle">
          {body}
        </Text>
      )}
      {action}
    </div>
  )
}

export function ErrorState({ error }: { error: unknown }) {
  const e = error as any
  const msg = e?.status === 403 ? "You don't have permission to see this." : e?.message ?? "Couldn't load this."
  return <EmptyState title="Something went wrong" body={msg} />
}

/** Debounced search box for list pages. */
export function SearchInput({ value, onChange, placeholder = "Search" }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => v !== value && onChange(v), 300)
    return () => clearTimeout(t)
  }, [v]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="relative w-full max-w-xs">
      {/* Medusa's search input draws its own magnifier. */}
      <Input type="search" size="small" value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} />
    </div>
  )
}

export type Column<T> = { key: string; header: ReactNode; cell: (row: T) => ReactNode; className?: string }

/** Server-paginated table: the page passes rows + count, and gets page changes back. */
export function DataTable<T>({
  columns,
  rows,
  count,
  pageSize,
  pageIndex,
  onPageChange,
  onRowClick,
  loading,
  empty,
  rowKey,
  mobile,
  selected,
  onSelectedChange,
  bulkActions,
}: {
  /** Row selection (Shopify bulk actions): pass both to show checkboxes. */
  selected?: Set<string>
  onSelectedChange?: (s: Set<string>) => void
  /** Rendered in the bar that replaces the header while rows are selected. */
  bulkActions?: ReactNode
  columns: Column<T>[]
  rows: T[] | undefined
  count: number
  pageSize: number
  pageIndex: number
  onPageChange: (i: number) => void
  onRowClick?: (row: T) => void
  loading?: boolean
  empty?: ReactNode
  rowKey: (row: T) => string
  /** Phone layout for one row (Shopify stacks rows on small screens). Defaults to the columns stacked. */
  mobile?: (row: T) => ReactNode
}) {
  const pageCount = Math.max(1, Math.ceil(count / pageSize))
  const selectable = !!selected && !!onSelectedChange
  const pageKeys = (rows ?? []).map(rowKey)
  const allOn = selectable && pageKeys.length > 0 && pageKeys.every((k) => selected!.has(k))
  const someOn = selectable && pageKeys.some((k) => selected!.has(k))
  const toggle = (k: string, on: boolean) => {
    const n = new Set(selected)
    on ? n.add(k) : n.delete(k)
    onSelectedChange!(n)
  }
  const toggleAll = (on: boolean) => {
    const n = new Set(selected)
    for (const k of pageKeys) on ? n.add(k) : n.delete(k)
    onSelectedChange!(n)
  }
  const box = (k: string) => (
    <span onClick={(e) => e.stopPropagation()} className="inline-flex">
      <Checkbox aria-label="Select row" checked={selected!.has(k)} onCheckedChange={(c) => toggle(k, c === true)} />
    </span>
  )
  const mobileRow =
    mobile ??
    ((r: T) => (
      <div className="flex flex-col gap-y-1">
        {columns.map((c, i) => {
          const v = c.cell(r)
          if (v == null || v === false || v === "") return null
          return i === 0 ? (
            <div key={c.key}>{v}</div>
          ) : (
            <div key={c.key} className="flex items-center justify-between gap-x-3 text-[13px]">
              {c.header ? <span className="text-ui-fg-muted">{c.header}</span> : <span />}
              <span className="text-right">{v}</span>
            </div>
          )
        })}
      </div>
    ))
  return (
    <div>
      {selectable && selected!.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b bg-ui-bg-base px-4 py-2">
          <Checkbox aria-label="Select all on this page" checked={allOn ? true : someOn ? "indeterminate" : false} onCheckedChange={(c) => toggleAll(c === true)} />
          <Text size="small" weight="plus">{selected!.size} selected</Text>
          <div className="ml-auto flex flex-wrap items-center gap-2">{bulkActions}</div>
        </div>
      )}
      {/* Phones: stacked rows */}
      <ul className="divide-y md:hidden">
        {loading && !rows
          ? Array.from({ length: 4 }).map((_, i) => (
              <li key={i} className="px-4 py-3">
                <Skeleton className="h-4 w-40" />
              </li>
            ))
          : rows?.map((r) => (
              <li
                key={rowKey(r)}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={"flex items-start gap-x-3 px-4 py-3 " + (onRowClick ? "cursor-pointer active:bg-ui-bg-base-hover" : "")}
              >
                {selectable && <span className="pt-1">{box(rowKey(r))}</span>}
                <div className="min-w-0 flex-1">{mobileRow(r)}</div>
              </li>
            ))}
      </ul>
      <div className="hidden overflow-x-auto md:block">
        <Table>
          <Table.Header>
            <Table.Row>
              {selectable && (
                <Table.HeaderCell className="w-10">
                  <Checkbox aria-label="Select all on this page" checked={allOn ? true : someOn ? "indeterminate" : false} onCheckedChange={(c) => toggleAll(c === true)} />
                </Table.HeaderCell>
              )}
              {columns.map((c) => (
                <Table.HeaderCell key={c.key} className={c.className}>
                  {c.header}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loading && !rows
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Table.Row key={i}>
                    {columns.map((c) => (
                      <Table.Cell key={c.key}>
                        <Skeleton className="h-4 w-24" />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              : rows?.map((r) => (
                  <Table.Row key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined} className={onRowClick ? "cursor-pointer" : undefined}>
                    {selectable && <Table.Cell className="w-10">{box(rowKey(r))}</Table.Cell>}
                    {columns.map((c) => (
                      <Table.Cell key={c.key} className={c.className}>
                        {c.cell(r)}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
          </Table.Body>
        </Table>
      </div>
      {!loading && rows?.length === 0 && (empty ?? <EmptyState title="Nothing here yet" />)}
      {count > pageSize && (
        <div className="sticky bottom-0 z-[1] rounded-b-[inherit] border-t border-[var(--tb-line,#e3e3e3)] bg-ui-bg-base">
        <Table.Pagination
          count={count}
          pageSize={pageSize}
          pageIndex={pageIndex}
          pageCount={pageCount}
          canPreviousPage={pageIndex > 0}
          canNextPage={pageIndex < pageCount - 1}
          previousPage={() => onPageChange(pageIndex - 1)}
          nextPage={() => onPageChange(pageIndex + 1)}
        />
        </div>
      )}
    </div>
  )
}

/** Order/product/discount states → Terabizz badge tone. */
const PILL: Record<string, { tone: Tone; dot?: "ring" | "filled"; label?: string }> = {
  published: { tone: "success", label: "Active" },
  active: { tone: "success" },
  depleted: { tone: "neutral" },
  expired: { tone: "warning" },
  disabled: { tone: "critical" },
  draft: { tone: "info" },
  archived: { tone: "neutral" },
  proposed: { tone: "warning", label: "Pending" },
  rejected: { tone: "critical" },
  // payments
  captured: { tone: "neutral", dot: "filled", label: "Paid" },
  paid: { tone: "neutral", dot: "filled" },
  authorized: { tone: "attention", dot: "ring" },
  not_paid: { tone: "warning", dot: "ring", label: "Payment pending" },
  awaiting: { tone: "warning", dot: "ring", label: "Payment pending" },
  pending: { tone: "warning", dot: "ring" },
  partially_refunded: { tone: "neutral", dot: "filled" },
  refunded: { tone: "neutral", dot: "filled" },
  requires_action: { tone: "critical", dot: "ring" },
  canceled: { tone: "neutral", label: "Canceled" },
  // fulfillment
  fulfilled: { tone: "neutral", dot: "filled" },
  delivered: { tone: "neutral", dot: "filled" },
  shipped: { tone: "info", dot: "filled", label: "In transit" },
  not_fulfilled: { tone: "attention", dot: "ring", label: "Unfulfilled" },
  partially_fulfilled: { tone: "attention", dot: "ring", label: "Partially fulfilled" },
  on_hold: { tone: "warning", dot: "ring", label: "On hold" },
  completed: { tone: "neutral", dot: "filled" },
}

export function Status({ value }: { value: string | null | undefined }) {
  if (!value) return <Text size="small">—</Text>
  const p = PILL[value] ?? { tone: "neutral" as Tone }
  const label = p.label ?? value.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase())
  return <Badge tone={p.tone} dot={p.dot}>{label}</Badge>
}

/** Page-index ⇄ URL search param so lists keep their page on back/forward. */
export function usePage(params: URLSearchParams, setParams: (p: URLSearchParams) => void) {
  const pageIndex = Math.max(0, Number(params.get("page") ?? 1) - 1)
  const q = params.get("q") ?? ""
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) v ? next.set(k, v) : next.delete(k)
    setParams(next)
  }
  return {
    pageIndex,
    q,
    setPage: (i: number) => set({ page: i > 0 ? String(i + 1) : null }),
    setQ: (v: string) => set({ q: v || null, page: null }),
  }
}

/** Shopify's contextual save bar: appears with unsaved changes. */
export function SaveBar({
  visible,
  message,
  error,
  onDiscard,
  saveLabel = "Save",
  discardLabel = "Discard",
  saving,
  disabled,
  onSave,
}: {
  /** Outside a <form>: called on Save (otherwise Save submits the form). */
  onSave?: () => void
  visible: boolean
  message: string
  error?: boolean
  onDiscard: () => void
  saveLabel?: string
  discardLabel?: string
  saving?: boolean
  disabled?: boolean
}) {
  if (!visible) return null
  return (
    <>
      {/* In-flow spacer: the bar is fixed, so without this the last controls on a page
          (inline Save buttons, the final field) can sit underneath it and be unclickable. */}
      <div aria-hidden className="h-24" />
      <div className="tf-savebar fixed inset-x-0 bottom-0 z-30 border-t border-ui-border-base bg-ui-bg-base shadow-elevation-flyout lg:bottom-0 lg:left-[var(--tf-content-left,15rem)] lg:right-0">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between gap-x-3 px-4 py-3 lg:px-8">
          <Text size="small" weight="plus" className={error ? "text-ui-fg-error" : "text-ui-fg-subtle"}>
            {message}
          </Text>
          <div className="flex gap-x-2">
            <Button type="button" size="small" variant="secondary" onClick={onDiscard}>
              {discardLabel}
            </Button>
            <Button type={onSave ? "button" : "submit"} onClick={onSave} size="small" isLoading={saving} disabled={disabled}>
              {saveLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

